import Close from "@mui/icons-material/Close";
import { Button } from "@mui/material";
import AutoComplete from "components/Common/Autocomplete/Autocomplete";
import TruckStopFormProvider from "components/Trucks/TruckStopFormProvider";
import {
	AddNewAddressLink,
	AddTruckStopText,
	AllFieldsRequiredText,
	CancelButtonText,
	SaveButtonText,
	SelectShippingAddressErrorText,
	SelectShippingAddressText,
	StopNumberText,
	UpdateTruckStopText
} from "constants/text";
import { useGetTrucksAccountsQuery } from "features/api/accountApi";
import AddNewAddressModal from "pages/Replacements/components/AddNewAddressModal/AddNewAddressModal";
import AddressFormProvider from "pages/Replacements/forms/AddressForm/AddressFormProvider";
import { useEffect, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "stores/application.store";
import { models } from "types/api/viewModels";
import { TruckStopModalWrapper } from "./TruckStopModal.styles";
import { truckActions } from "features/reducers/truck/truck";
import { useNewOrderShipToAddressesMutation } from "features/api/newOrderApi";
import { AddAddress } from "components/Replacements/replacementShipToForm.styles";
import { AddTruckStopFields } from "../truckStopSchema";
import { v4 as uuidv4 } from "uuid";
import { useDialog } from "hooks/useDialog.ts";
import { dialogStateActions } from "features/reducers/dialogState/dialogState.ts";

const TruckStopModal = () => {
	const stops = useSelector((state: RootState) => state.truck.truck?.stops);
	const { dialogIndex: index } = useDialog();

	const currentStop = !index && index !== 0 ? undefined : stops?.[index];
	return (
		<TruckStopFormProvider
			defaultValues={{
				stop: {
					address: currentStop?.address as models["AddressViewModel"],
					shipToId: currentStop?.shipToId
				}
			}}
		>
			<TruckStopModalContent />
		</TruckStopFormProvider>
	);
};

const TruckStopModalContent = () => {
	const [newOrderShipToAddresses, { data: addresses, isLoading: addressesLoading }] =
		useNewOrderShipToAddressesMutation();
	const { data: accounts, isLoading: isAccountsLoading } = useGetTrucksAccountsQuery("");
	const { handleSubmit, clearErrors, setValue } = useFormContext<AddTruckStopFields>();

	const stops = useSelector((state: RootState) => state.truck.truck?.stops);
	const { dialogIndex: index } = useDialog();

	const currentStop = stops?.[index ?? 0];
	const { closeDialog, setSaveTruckErrorMessage } = useDialog();
	const onClose = () => {
		closeDialog();
		setSaveTruckErrorMessage(undefined);
	};

	const dispatch = useDispatch();
	const truck = useSelector((state: RootState) => state.truck.truck);
	if (index === undefined || index === null) {
		dispatch(dialogStateActions.setDialogIndex(truck?.stops?.length ? truck.stops.length : 0));
	}

	const isUpdate = (index || index === 0) && index < (truck?.stops?.length ?? 0);

	const [customAddresses, setCustomAddresses] = useState<models["ShipToViewModel"][]>(
		currentStop ? [{ ...currentStop, isSelected: true, isActive: true }] : []
	);
	const [isNewAddressModalOpen, setIsNewAddressModalOpen] = useState(false);

	const handleSaveStop = ({ stop }: AddTruckStopFields) => {
		if (isUpdate) {
			dispatch(truckActions.updateStop({ index, stop }));
		} else {
			dispatch(
				truckActions.addStop({
					truckStopId: stop.truckStopId || uuidv4(),
					...stop
				})
			);
		}
		handleOnClose();
	};

	const handleOnClose = () => {
		onClose();
		clearErrors("stop");
	};

	useEffect(() => {
		const foundSelectedAccount = accounts?.find((account) => account.accountId === truck?.accountId);
		newOrderShipToAddresses(
			foundSelectedAccount?.billTos?.map((billTo) => ({
				billToId: billTo.billToId,
				accountId: truck?.accountId
			})) ?? []
		)
			.unwrap()
			.catch((err) => err);
	}, [accounts, truck?.accountId, newOrderShipToAddresses]);

	return (
		<TruckStopModalWrapper
			open
			onClose={handleOnClose}
			data-testid="truck-add-stop-modal"
		>
			<form
				onSubmit={handleSubmit(handleSaveStop)}
				noValidate
			>
				<div>
					<h6>{isUpdate ? UpdateTruckStopText : AddTruckStopText}</h6>
					<Close onClick={handleOnClose} />
				</div>
				<div>
					<span>{AllFieldsRequiredText}</span>
					<div>
						<span>
							{StopNumberText}
							{!index && index !== 0 ? 0 : index + 1}
						</span>
						<Controller
							shouldUnregister // Clear the form when the component is unmounted
							name="stop"
							defaultValue={currentStop}
							render={({ field: { onChange, value = null }, fieldState: { error } }) => (
								<AutoComplete
									options={
										// Deduplicate custom addresses from existing ones
										customAddresses.reduce(
											(
												accumulator: models["TruckStopViewModel"][],
												customAddress: models["TruckStopViewModel"]
											) => {
												if (
													accumulator.find(
														(stop) =>
															stop.address?.name === customAddress.address?.name &&
															stop.address?.line1 === customAddress.address?.line1 &&
															stop.address?.line2 === customAddress.address?.line2 &&
															stop.address?.line3 === customAddress.address?.line3 &&
															stop.address?.mailStop ===
																customAddress.address?.mailStop &&
															stop.address?.phoneNumber ===
																customAddress.address?.phoneNumber &&
															stop.address?.email === customAddress.address?.email &&
															stop.address?.city === customAddress.address?.city &&
															stop.address?.state === customAddress.address?.state &&
															stop.address?.zip === customAddress.address?.zip &&
															stop.address?.county === customAddress.address?.county
													) === undefined
												) {
													return [...accumulator, customAddress];
												}
												return accumulator;
											},
											addresses ?? []
										)
									}
									onChange={(_, selectedValue) => {
										onChange(selectedValue);
									}}
									getOptionLabel={(option: models["TruckStopViewModel"]) =>
										option.address
											? `${option.address.name}, ${option.address.line1}, ${option.address.city}, ${option.address.state} ${option.address.zip}`
											: ""
									}
									isError={!!error}
									errorText={SelectShippingAddressErrorText}
									label={SelectShippingAddressText}
									dataTestId="truck-info-shipping-address"
									isLoading={isAccountsLoading || addressesLoading}
									required
									value={value}
								/>
							)}
						/>
						<div>
							<AddAddress
								onClick={() => setIsNewAddressModalOpen(true)}
								data-id="truck-add-address-button"
							>
								{AddNewAddressLink}
							</AddAddress>
						</div>
						<AddressFormProvider>
							<AddNewAddressModal
								handleClose={() => setIsNewAddressModalOpen(false)}
								open={isNewAddressModalOpen}
								onSubmit={(address) => {
									const shippingAddress: models["ShipToViewModel"] = {
										shipToId: null,
										isActive: true,
										isSelected: true,
										address: {
											name: address.fullName,
											email: address.email,
											phoneNumber: address.contactPhoneNumber,
											line1: address.address,
											line2: address.address2,
											line3: null,
											city: address.city,
											state: address.state,
											zip: address.zip,
											county: address.county
										}
									};
									setCustomAddresses((prev) => [...prev, shippingAddress]);
									setIsNewAddressModalOpen(false);
									setValue("stop", {
										address: shippingAddress.address!,
										shipToId: ""
									});
								}}
							/>
						</AddressFormProvider>
					</div>
					<div>
						<Button
							data-testid="trucks-add-orders-back-button"
							variant="outlined"
							onClick={handleOnClose}
						>
							{CancelButtonText}
						</Button>
						<Button
							data-testid="trucks-add-orders-continue-button"
							type="submit"
							variant="contained"
						>
							{SaveButtonText}
						</Button>
					</div>
				</div>
			</form>
		</TruckStopModalWrapper>
	);
};

export default TruckStopModal;
